import { useEffect, useState } from "react";
import { Palette, Save, RotateCcw, ExternalLink, Eye, EyeOff, Plus, Trash2, Sparkles, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

import { THEMES as APP_THEMES } from "@/lib/theme";

const THEMES = APP_THEMES.map((t) => ({ id: t.id, label: t.name, swatch: t.swatch, bg: t.bg }));

const ACCENT_PRESETS = [
  { name: "Coral (default)", color: "#E07A5F", hover: "#C96A51" },
  { name: "Electric blue",   color: "#0052FF", hover: "#0040CC" },
  { name: "Forest",          color: "#2F6F4E", hover: "#245A3F" },
  { name: "Sunset",          color: "#F4A261", hover: "#E08A4F" },
  { name: "Plum",            color: "#7B2D8E", hover: "#62236F" },
  { name: "Rose",            color: "#D6336C", hover: "#B82A5C" },
  { name: "Graphite",        color: "#222831", hover: "#0E1318" },
];

const SECTIONS = [
  { id: "hero", label: "Hero" },
  { id: "manifesto", label: "Manifiesto" },
  { id: "dual_feed", label: "Feed Dual" },
  { id: "monetization", label: "Monetización" },
  { id: "ai_travel", label: "Viajes IA" },
  { id: "market_communities", label: "Marketplace + Comunidades" },
  { id: "privacy", label: "Privacidad" },
  { id: "comparison", label: "Comparación" },
  { id: "investors", label: "Inversores" },
  { id: "cta_footer", label: "CTA Footer" },
];

export default function SiteEditorTab() {
  const auth = useAdminAuth();
  const site = useSiteConfig();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState("colors");
  const [presets, setPresets] = useState([]);
  const [applyingPreset, setApplyingPreset] = useState(null);
  const [scheduled, setScheduled] = useState([]);
  const [schedForm, setSchedForm] = useState({ preset_id: "halloween", start_at: "", end_at: "", label: "" });
  const [schedBusy, setSchedBusy] = useState(false);

  const loadScheduled = () => {
    auth.apiGet("/admin/site-config/scheduled-presets")
      .then((r) => setScheduled(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    auth.apiGet("/admin/site-config")
      .then((r) => setDraft(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Error al cargar"));
    auth.apiGet("/admin/site-config/presets")
      .then((r) => setPresets(r.data))
      .catch(() => {});
    loadScheduled();
  }, [auth]); // eslint-disable-line

  if (!draft) return <div className="text-sm text-ink-mid">Cargando…</div>;

  const set = (path, value) => {
    setDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!(keys[i] in cur) || cur[keys[i]] === null) cur[keys[i]] = {};
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await auth.apiPatch("/admin/site-config", draft);
      setDraft(data);
      site.applyVisuals(data);
      site.reload();
      toast.success("Cambios publicados — sitio actualizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("¿Restaurar el sitio a sus valores por defecto? Esta acción no se puede deshacer.")) return;
    try {
      const { data } = await auth.apiPost("/admin/site-config/reset", {});
      setDraft(data);
      site.applyVisuals(data);
      site.reload();
      toast.success("Sitio restaurado a valores por defecto");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const applyPreset = async (preset) => {
    if (!window.confirm(`¿Aplicar el preset "${preset.label}"? Cambiará tema y colores del sitio público al instante.`)) return;
    setApplyingPreset(preset.id);
    try {
      const { data } = await auth.apiPost(`/admin/site-config/presets/${preset.id}/apply`, {});
      setDraft(data.config);
      site.applyVisuals(data.config);
      site.reload();
      toast.success(`Preset aplicado · ${data.label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aplicar preset");
    } finally {
      setApplyingPreset(null);
    }
  };

  const createSchedule = async (e) => {
    e.preventDefault();
    if (!schedForm.start_at || !schedForm.end_at) {
      toast.error("Indica fecha inicio y fin");
      return;
    }
    setSchedBusy(true);
    try {
      // Convert datetime-local (local TZ) to ISO with UTC offset
      const startISO = new Date(schedForm.start_at).toISOString();
      const endISO = new Date(schedForm.end_at).toISOString();
      await auth.apiPost("/admin/site-config/scheduled-presets", {
        preset_id: schedForm.preset_id,
        start_at: startISO,
        end_at: endISO,
        label: schedForm.label || undefined,
      });
      toast.success("Programación creada");
      setSchedForm({ preset_id: "halloween", start_at: "", end_at: "", label: "" });
      loadScheduled();
      // Reload draft in case the schedule is active and applied immediately
      const { data } = await auth.apiGet("/admin/site-config");
      setDraft(data);
      site.reload();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Error al crear");
    } finally {
      setSchedBusy(false);
    }
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm("¿Eliminar esta programación?")) return;
    try {
      await auth.apiDelete(`/admin/site-config/scheduled-presets/${id}`);
      toast.success("Programación eliminada");
      loadScheduled();
      const { data } = await auth.apiGet("/admin/site-config");
      setDraft(data);
      site.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const sectionsVisible = draft.landing?.sections_visible || {};
  const stats = draft.landing?.stats || [];
  const marquee = draft.landing?.marquee || [];
  const items = draft.landing?.manifesto?.items || [];

  return (
    <div data-testid="tab-site-editor" className="space-y-6">
      {/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3 sticky top-[8.5rem] bg-cream/95 backdrop-blur-sm border hairline rounded-2xl p-3 z-10">
        <div className="flex items-center gap-3 flex-wrap">
          <Palette className="w-4 h-4 text-coral" />
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral">Editor visual no-code</p>
          {draft.updated_at && (
            <p className="text-[11px] text-ink-mid">
              Última edición · {new Date(draft.updated_at).toLocaleString()} {draft.updated_by ? `· ${draft.updated_by}` : ""}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <a href="/landing" target="_blank" rel="noreferrer" data-testid="site-preview-btn"
             className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border hairline hover:border-coral transition">
            <ExternalLink className="w-3.5 h-3.5" /> Vista previa
          </a>
          <button onClick={reset} data-testid="site-reset-btn"
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border hairline hover:border-red-500 hover:text-red-700 transition">
            <RotateCcw className="w-3.5 h-3.5" /> Restaurar
          </button>
          <button onClick={save} disabled={saving} data-testid="site-save-btn"
                  className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full bg-ink text-cream hover:bg-coral transition disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {saving ? "Guardando…" : "Publicar cambios"}
          </button>
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: "colors", label: "Colores y tema" },
          { id: "typography", label: "Tipografía" },
          { id: "style", label: "Estilo" },
          { id: "presets", label: "Presets de marca" },
          { id: "scheduled", label: "Programados" },
          { id: "brand", label: "Marca" },
          { id: "hero", label: "Hero" },
          { id: "manifesto", label: "Manifiesto" },
          { id: "stats", label: "Stats / Marquee" },
          { id: "sections", label: "Secciones visibles" },
          { id: "home_modules", label: "Módulos Home" },
          { id: "legal", label: "Legal y Misión" },
        ].map((s) => (
          <button key={s.id} onClick={() => setSection(s.id)}
                  data-testid={`site-section-${s.id}`}
                  className={`text-xs px-3 py-1.5 rounded-full border hairline transition
                    ${section === s.id ? "bg-ink text-cream border-ink" : "bg-cream-card hover:border-coral"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* SCHEDULED */}
      {section === "scheduled" && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card title="Nueva programación">
            <p className="text-xs text-ink-mid mb-3">
              El preset se aplica automáticamente en la fecha de inicio y vuelve al preset por defecto al final. Útil para Halloween, Navidad, lanzamientos.
            </p>
            <form onSubmit={createSchedule} className="space-y-3">
              <Sel testid="sched-preset" label="Preset" value={schedForm.preset_id}
                   onChange={(v) => setSchedForm({ ...schedForm, preset_id: v })}
                   options={presets.filter((p) => p.id !== "default").map((p) => ({ id: p.id, label: p.label }))} />
              <Field label="Etiqueta (opcional)" testid="sched-label"
                     value={schedForm.label} onChange={(v) => setSchedForm({ ...schedForm, label: v })} />
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">Inicio</span>
                  <input type="datetime-local" value={schedForm.start_at}
                         onChange={(e) => setSchedForm({ ...schedForm, start_at: e.target.value })}
                         data-testid="sched-start-at" required
                         className="w-full bg-cream border hairline rounded-xl px-3 py-2 text-sm outline-none focus:border-coral" />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">Fin</span>
                  <input type="datetime-local" value={schedForm.end_at}
                         onChange={(e) => setSchedForm({ ...schedForm, end_at: e.target.value })}
                         data-testid="sched-end-at" required
                         className="w-full bg-cream border hairline rounded-xl px-3 py-2 text-sm outline-none focus:border-coral" />
                </label>
              </div>
              <button type="submit" disabled={schedBusy} data-testid="sched-submit"
                      className="w-full inline-flex items-center justify-center gap-1.5 bg-ink text-cream rounded-full py-2.5 text-sm hover:bg-coral transition disabled:opacity-50">
                <CalendarClock className="w-3.5 h-3.5" /> {schedBusy ? "Creando…" : "Programar"}
              </button>
            </form>
          </Card>

          <Card title={`Programaciones · ${scheduled.length}`}>
            {scheduled.length === 0 ? (
              <p className="text-sm text-ink-mid">No hay programaciones todavía.</p>
            ) : (
              <div className="space-y-2 max-h-[480px] overflow-auto">
                {scheduled.map((s) => (
                  <div key={s.id} data-testid={`sched-row-${s.id}`}
                       className="border hairline rounded-xl p-3 bg-cream-card flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{s.label}</span>
                        <StatusBadge status={s.status} />
                        <span className="text-[10px] font-mono text-ink-mid">{s.preset_label}</span>
                      </div>
                      <p className="text-[11px] text-ink-mid mt-1 font-mono">
                        {new Date(s.start_at).toLocaleString()} → {new Date(s.end_at).toLocaleString()}
                      </p>
                    </div>
                    <button onClick={() => deleteSchedule(s.id)} data-testid={`sched-delete-${s.id}`}
                            className="text-red-700 hover:bg-red-50 rounded p-1 shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* PRESETS */}
      {section === "presets" && (
        <Card title="Presets de marca · campañas y temporadas">
          <p className="text-sm text-ink-mid -mt-1 mb-4">
            Aplica un set completo (tema + colores + textos del hero) con un click. Ideal para campañas (Halloween, Navidad, lanzamientos). Después puedes ajustar detalles en las otras pestañas.
          </p>
          {draft.last_preset_applied && (
            <p className="text-[11px] font-mono uppercase tracking-wider text-coral mb-3">
              ◊ Último preset aplicado · {draft.last_preset_applied}
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {presets.map((p) => {
              const isLast = draft.last_preset_applied === p.id;
              const isApplying = applyingPreset === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => applyPreset(p)}
                  disabled={isApplying}
                  data-testid={`site-preset-${p.id}`}
                  className={`relative text-left p-4 rounded-2xl border-2 transition-all
                    ${isLast ? "border-coral shadow-md" : "border-transparent hairline border bg-cream-card hover:border-coral"}
                    ${isApplying ? "opacity-60 cursor-wait" : ""}`}
                >
                  {isLast && (
                    <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-coral">
                      <Sparkles className="w-3 h-3" /> activo
                    </span>
                  )}
                  <div className="flex gap-1.5 mb-3">
                    {p.swatches.map((c, i) => (
                      <span key={i} className="w-8 h-8 rounded-lg border hairline" style={{ background: c }} />
                    ))}
                  </div>
                  <p className="font-serif text-xl tracking-tight">{p.label}</p>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-ink-mid mt-1">{p.sub}</p>
                  <p className="mt-3 inline-flex items-center gap-1 text-[11px] text-coral font-mono">
                    {isApplying ? "Aplicando…" : "Aplicar →"}
                  </p>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* TYPOGRAPHY */}
      {section === "typography" && (
        <Card title="Tipografía">
          <p className="text-xs text-ink-mid -mt-1 mb-3">Las fuentes se cargan desde Google Fonts y se aplican en todo el sitio en tiempo real.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Sel testid="typo-heading" label="Fuente de títulos"
                 value={draft.typography?.font_heading || "Playfair Display"}
                 onChange={(v) => set("typography.font_heading", v)}
                 options={["Playfair Display","Inter","Roboto","Poppins","Montserrat","Open Sans","Lato","Arial","Georgia","Merriweather"].map((f) => ({ id: f, label: f }))} />
            <Sel testid="typo-body" label="Fuente de texto"
                 value={draft.typography?.font_body || "Inter"}
                 onChange={(v) => set("typography.font_body", v)}
                 options={["Inter","Roboto","Poppins","Montserrat","Open Sans","Lato","Arial","Georgia","Merriweather"].map((f) => ({ id: f, label: f }))} />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field testid="typo-size-base" label="Tamaño base (px)" type="number"
                   value={draft.typography?.font_size_base ?? 16}
                   onChange={(v) => set("typography.font_size_base", Number(v) || 16)} />
            <Field testid="typo-h1-scale" label="Escala títulos (H1)"
                   value={draft.typography?.font_size_h1_scale ?? 1}
                   onChange={(v) => set("typography.font_size_h1_scale", Number(v) || 1)} />
            <Field testid="typo-letter" label="Espaciado (×100)" type="number"
                   value={draft.typography?.letter_spacing ?? 0}
                   onChange={(v) => set("typography.letter_spacing", Number(v) || 0)} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="typo-bold-titles"
                   checked={!!draft.typography?.bold_titles}
                   onChange={(e) => set("typography.bold_titles", e.target.checked)}
                   className="accent-coral" />
            Negrita automática en títulos
          </label>
          <div className="mt-4 p-4 rounded-xl border hairline bg-cream">
            <p className="text-[10px] font-mono uppercase tracking-wider text-ink-mid mb-2">Vista previa</p>
            <p style={{ fontFamily: `"${draft.typography?.font_heading || "Playfair Display"}", serif`,
                        fontSize: `${(draft.typography?.font_size_base ?? 16) * 2.5 * (draft.typography?.font_size_h1_scale ?? 1)}px`,
                        fontWeight: draft.typography?.bold_titles ? 600 : 400,
                        letterSpacing: `${(draft.typography?.letter_spacing ?? 0) / 100}em` }}
               className="leading-tight">
              Tu tiempo tiene valor.
            </p>
            <p style={{ fontFamily: `"${draft.typography?.font_body || "Inter"}", sans-serif`,
                        fontSize: `${draft.typography?.font_size_base ?? 16}px`,
                        letterSpacing: `${(draft.typography?.letter_spacing ?? 0) / 100}em` }}
               className="text-ink-mid mt-2">
              La nueva red social. Construida sobre lo que la gente realmente necesita.
            </p>
          </div>
        </Card>
      )}

      {/* STYLE */}
      {section === "style" && (
        <Card title="Estilo visual">
          <div className="grid sm:grid-cols-3 gap-3">
            <Sel testid="style-mode" label="Modo claro/oscuro"
                 value={draft.style?.mode || "light"}
                 onChange={(v) => set("style.mode", v)}
                 options={[{ id: "light", label: "Claro" }, { id: "dark", label: "Oscuro" }]} />
            <Sel testid="style-design" label="Estilo general"
                 value={draft.style?.design_style || "modern"}
                 onChange={(v) => set("style.design_style", v)}
                 options={[{ id: "modern", label: "Moderno" }, { id: "corporate", label: "Corporativo" }, { id: "minimal", label: "Minimalista" }]} />
            <Sel testid="style-button-size" label="Tamaño de botones"
                 value={draft.style?.button_size || "md"}
                 onChange={(v) => set("style.button_size", v)}
                 options={[{ id: "sm", label: "Pequeño" }, { id: "md", label: "Medio" }, { id: "lg", label: "Grande" }]} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field testid="style-radius" label="Bordes redondos (0=cuadrado, 1=normal, 1.5=píldora)"
                   value={draft.style?.border_radius_scale ?? 1}
                   onChange={(v) => set("style.border_radius_scale", Math.max(0, Number(v) || 0))} />
            <Sel testid="style-shadow" label="Intensidad de sombras"
                 value={draft.style?.shadow_intensity || "soft"}
                 onChange={(v) => set("style.shadow_intensity", v)}
                 options={[{ id: "none", label: "Sin sombras" }, { id: "soft", label: "Suaves" }, { id: "strong", label: "Fuertes" }]} />
          </div>
          <Field testid="style-secondary" label="Color secundario (hex)"
                 value={draft.style?.secondary_color || "#3D5A80"}
                 onChange={(v) => set("style.secondary_color", v)} />
        </Card>
      )}

      {/* HOME MODULES */}
      {section === "home_modules" && (
        <Card title="Módulos del Home — visibles, orden, nombres e iconos">
          <p className="text-xs text-ink-mid -mt-1 mb-3">
            Personaliza qué categorías aparecen en la portada y en qué orden. Cambia nombres y suben/bajan con las flechas.
          </p>
          <div className="space-y-2">
            {(draft.home_modules || []).slice().sort((a, b) => a.order - b.order).map((m, idx, arr) => (
              <div key={m.id} data-testid={`home-mod-${m.id}`}
                   className={`grid grid-cols-12 gap-2 items-center p-3 border hairline rounded-xl ${m.visible ? "bg-cream-card" : "bg-zinc-100 opacity-70"}`}>
                <input type="checkbox" checked={!!m.visible}
                       onChange={(e) => set("home_modules", arr.map((x) => x.id === m.id ? { ...x, visible: e.target.checked } : x))}
                       data-testid={`home-mod-${m.id}-visible`}
                       className="col-span-1 accent-coral" />
                <input value={m.label} data-testid={`home-mod-${m.id}-label`}
                       onChange={(e) => set("home_modules", arr.map((x) => x.id === m.id ? { ...x, label: e.target.value } : x))}
                       className="col-span-5 bg-cream border hairline rounded-lg px-2 py-1.5 text-sm outline-none focus:border-coral" />
                <input value={m.icon} data-testid={`home-mod-${m.id}-icon`}
                       onChange={(e) => set("home_modules", arr.map((x) => x.id === m.id ? { ...x, icon: e.target.value } : x))}
                       placeholder="Lucide icon name"
                       className="col-span-3 bg-cream border hairline rounded-lg px-2 py-1.5 text-xs font-mono outline-none focus:border-coral" />
                <div className="col-span-3 flex gap-1 justify-end">
                  <button type="button" disabled={idx === 0} onClick={() => {
                    const reordered = [...arr];
                    [reordered[idx - 1], reordered[idx]] = [reordered[idx], reordered[idx - 1]];
                    set("home_modules", reordered.map((x, i) => ({ ...x, order: i })));
                  }} className="px-2 py-1 text-xs border hairline rounded hover:border-coral disabled:opacity-30">↑</button>
                  <button type="button" disabled={idx === arr.length - 1} onClick={() => {
                    const reordered = [...arr];
                    [reordered[idx + 1], reordered[idx]] = [reordered[idx], reordered[idx + 1]];
                    set("home_modules", reordered.map((x, i) => ({ ...x, order: i })));
                  }} className="px-2 py-1 text-xs border hairline rounded hover:border-coral disabled:opacity-30">↓</button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* LEGAL */}
      {section === "legal" && (
        <div className="space-y-4">
          <Card title="Empresa y misión">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field testid="legal-company-name" label="Razón social"
                     value={draft.legal?.company?.legal_name || ""}
                     onChange={(v) => set("legal.company.legal_name", v)} />
              <Field testid="legal-company-country" label="País"
                     value={draft.legal?.company?.country || ""}
                     onChange={(v) => set("legal.company.country", v)} />
              <Field testid="legal-company-contact" label="Email contacto legal"
                     value={draft.legal?.company?.contact_email || ""}
                     onChange={(v) => set("legal.company.contact_email", v)} />
              <Field testid="legal-company-support" label="Email soporte"
                     value={draft.legal?.company?.support_email || ""}
                     onChange={(v) => set("legal.company.support_email", v)} />
            </div>
            <Area testid="legal-mission" label="Misión"
                  value={draft.legal?.mission || ""}
                  onChange={(v) => set("legal.mission", v)} />
            <Area testid="legal-vision" label="Visión"
                  value={draft.legal?.vision || ""}
                  onChange={(v) => set("legal.vision", v)} />
            <p className="font-mono text-[10px] uppercase tracking-wider text-coral mt-3">Valores</p>
            {(draft.legal?.values || []).map((v, i) => (
              <div key={i} className="border hairline rounded-xl p-3 bg-cream-card">
                <Field label="Título" value={v.title} onChange={(val) => {
                  const next = [...(draft.legal?.values || [])];
                  next[i] = { ...next[i], title: val };
                  set("legal.values", next);
                }} />
                <Area label="Descripción" value={v.desc} onChange={(val) => {
                  const next = [...(draft.legal?.values || [])];
                  next[i] = { ...next[i], desc: val };
                  set("legal.values", next);
                }} />
                <button type="button" onClick={() => set("legal.values", (draft.legal?.values || []).filter((_, j) => j !== i))}
                        className="text-xs text-red-700 hover:underline">Eliminar valor</button>
              </div>
            ))}
            <button type="button" onClick={() => set("legal.values", [...(draft.legal?.values || []), { title: "Nuevo valor", desc: "" }])}
                    data-testid="legal-add-value"
                    className="text-xs px-3 py-1.5 rounded-full border hairline hover:border-coral inline-flex items-center gap-1">
              <Plus className="w-3 h-3" /> Añadir valor
            </button>
          </Card>

          <Card title="Documentos legales (Markdown)">
            <p className="text-xs text-ink-mid -mt-1 mb-3">
              Edita Términos, Privacidad, Cookies y Política de Uso. Soporta Markdown básico (# títulos, ## subtítulos, **negrita**, listas con -).
            </p>
            <Area testid="legal-terms-md" label="Términos y Condiciones" value={draft.legal?.terms_md || ""} onChange={(v) => set("legal.terms_md", v)} />
            <Area testid="legal-privacy-md" label="Política de Privacidad" value={draft.legal?.privacy_md || ""} onChange={(v) => set("legal.privacy_md", v)} />
            <Area testid="legal-policy-md" label="Política de Uso" value={draft.legal?.policy_md || ""} onChange={(v) => set("legal.policy_md", v)} />
            <Area testid="legal-cookies-md" label="Política de Cookies" value={draft.legal?.cookies_md || ""} onChange={(v) => set("legal.cookies_md", v)} />
          </Card>
        </div>
      )}

      {/* COLORS */}
      {section === "colors" && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card title="Tema base">
            <div className="grid grid-cols-3 gap-2">
              {THEMES.map((t) => (
                <button key={t.id} onClick={() => set("theme", t.id)}
                        data-testid={`site-theme-${t.id}`}
                        className={`p-3 rounded-xl border-2 text-left transition
                          ${draft.theme === t.id ? "border-coral" : "border-transparent hairline border bg-cream-card hover:border-coral"}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full border hairline" style={{ background: t.bg }} />
                    <span className="w-4 h-4 rounded-full" style={{ background: t.swatch }} />
                  </div>
                  <p className="mt-2 text-xs font-medium">{t.label}</p>
                </button>
              ))}
            </div>
          </Card>

          <Card title="Color de acento">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Color principal (hex)" testid="site-accent-input"
                     value={draft.accent_color} onChange={(v) => set("accent_color", v)} />
              <Field label="Hover (hex)" testid="site-accent-hover-input"
                     value={draft.accent_color_hover || ""} onChange={(v) => set("accent_color_hover", v)} />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {ACCENT_PRESETS.map((p) => (
                <button key={p.color} onClick={() => { set("accent_color", p.color); set("accent_color_hover", p.hover); }}
                        data-testid={`site-accent-preset-${p.color.replace("#","")}`}
                        title={p.name}
                        className="w-8 h-8 rounded-full border-2 border-cream hover:scale-110 transition"
                        style={{ background: p.color, boxShadow: `0 0 0 1px ${p.color}33` }} />
              ))}
            </div>
            <div className="mt-4 p-3 rounded-xl border hairline bg-cream-card">
              <p className="text-[10px] font-mono uppercase tracking-wider text-ink-mid mb-2">Vista previa</p>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1.5 rounded-full text-xs text-white" style={{ background: draft.accent_color }}>Botón</span>
                <span className="text-sm" style={{ color: draft.accent_color }}>● Texto destacado</span>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* BRAND */}
      {section === "brand" && (
        <Card title="Identidad de marca">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Nombre" testid="site-brand-name"
                   value={draft.brand?.name || ""} onChange={(v) => set("brand.name", v)} />
            <Field label="Tagline" testid="site-brand-tagline"
                   value={draft.brand?.tagline || ""} onChange={(v) => set("brand.tagline", v)} />
          </div>
          <Field label="URL del logo" testid="site-brand-logo"
                 value={draft.brand?.logo_url || ""} onChange={(v) => set("brand.logo_url", v)} />
          {draft.brand?.logo_url && (
            <div className="mt-3 p-3 rounded-xl border hairline bg-white inline-flex">
              <img src={draft.brand.logo_url} alt="Logo" className="h-10" />
            </div>
          )}
        </Card>
      )}

      {/* HERO */}
      {section === "hero" && (
        <Card title="Hero (portada)">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Eyebrow (etiqueta superior)" testid="site-hero-eyebrow"
                   value={draft.landing?.hero?.eyebrow || ""} onChange={(v) => set("landing.hero.eyebrow", v)} />
            <Field label="Título (parte superior)" testid="site-hero-title-top"
                   value={draft.landing?.hero?.title_top || ""} onChange={(v) => set("landing.hero.title_top", v)} />
          </div>
          <Field label="Título — parte cursiva" testid="site-hero-title-bottom"
                 value={draft.landing?.hero?.title_bottom_italic || ""} onChange={(v) => set("landing.hero.title_bottom_italic", v)} />
          <Area label="Subtítulo" testid="site-hero-subtitle"
                value={draft.landing?.hero?.subtitle || ""} onChange={(v) => set("landing.hero.subtitle", v)} />
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Etiqueta posicionamiento" testid="site-hero-pos-label"
                   value={draft.landing?.hero?.positioning_label || ""} onChange={(v) => set("landing.hero.positioning_label", v)} />
            <Field label="Texto posicionamiento" testid="site-hero-pos-text"
                   value={draft.landing?.hero?.positioning_text || ""} onChange={(v) => set("landing.hero.positioning_text", v)} />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Etiqueta beta" testid="site-hero-beta-label"
                   value={draft.landing?.hero?.beta_label || ""} onChange={(v) => set("landing.hero.beta_label", v)} />
            <Field label="Texto beta (admite saltos de línea)" testid="site-hero-beta-text"
                   value={draft.landing?.hero?.beta_text || ""} onChange={(v) => set("landing.hero.beta_text", v)} />
          </div>
          <Area label="Texto manifiesto (intro lateral)" testid="site-hero-manifesto-intro"
                value={draft.landing?.hero?.manifesto_intro || ""} onChange={(v) => set("landing.hero.manifesto_intro", v)} />
        </Card>
      )}

      {/* MANIFESTO */}
      {section === "manifesto" && (
        <Card title="Manifiesto">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Eyebrow" testid="site-manifesto-eyebrow"
                   value={draft.landing?.manifesto?.eyebrow || ""} onChange={(v) => set("landing.manifesto.eyebrow", v)} />
          </div>
          <Area label="Intro" testid="site-manifesto-intro"
                value={draft.landing?.manifesto?.intro || ""} onChange={(v) => set("landing.manifesto.intro", v)} />
          <p className="text-[10px] font-mono uppercase tracking-wider text-coral mt-4 mb-2">Principios</p>
          {items.map((it, i) => (
            <div key={i} data-testid={`site-manifesto-item-${i}`} className="border hairline rounded-xl p-3 mb-2 bg-cream-card">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] font-mono text-coral">{it.n}</span>
                <button onClick={() => {
                  const next = items.filter((_, idx) => idx !== i);
                  set("landing.manifesto.items", next);
                }} data-testid={`site-manifesto-item-${i}-delete`}
                  className="ml-auto text-red-700 hover:bg-red-50 rounded p-1"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <Field label="Número" value={it.n} onChange={(v) => {
                const next = [...items]; next[i] = { ...next[i], n: v }; set("landing.manifesto.items", next);
              }} />
              <Field label="Título" value={it.t} onChange={(v) => {
                const next = [...items]; next[i] = { ...next[i], t: v }; set("landing.manifesto.items", next);
              }} />
              <Area label="Descripción" value={it.d} onChange={(v) => {
                const next = [...items]; next[i] = { ...next[i], d: v }; set("landing.manifesto.items", next);
              }} />
            </div>
          ))}
          <button onClick={() => set("landing.manifesto.items", [...items, { n: String(items.length + 1).padStart(2, "0"), t: "", d: "" }])}
                  data-testid="site-manifesto-add"
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border hairline hover:border-coral transition">
            <Plus className="w-3.5 h-3.5" /> Añadir principio
          </button>
        </Card>
      )}

      {/* STATS / MARQUEE */}
      {section === "stats" && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card title="Stats (4 destacados)">
            {stats.map((s, i) => (
              <div key={i} className="grid grid-cols-2 gap-2 mb-2">
                <Field label={`#${i + 1} valor`} value={s.k} onChange={(v) => {
                  const next = [...stats]; next[i] = { ...next[i], k: v }; set("landing.stats", next);
                }} />
                <Field label="Etiqueta" value={s.v} onChange={(v) => {
                  const next = [...stats]; next[i] = { ...next[i], v }; set("landing.stats", next);
                }} />
              </div>
            ))}
          </Card>
          <Card title="Marquee inferior (frases)">
            {marquee.map((t, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input value={t} onChange={(e) => {
                  const next = [...marquee]; next[i] = e.target.value; set("landing.marquee", next);
                }} className="flex-1 bg-cream border hairline rounded-xl px-3 py-2 text-sm outline-none focus:border-coral" />
                <button onClick={() => set("landing.marquee", marquee.filter((_, idx) => idx !== i))}
                        className="text-red-700 hover:bg-red-50 rounded p-1"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <button onClick={() => set("landing.marquee", [...marquee, "Nueva frase"])}
                    data-testid="site-marquee-add"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border hairline hover:border-coral transition mt-2">
              <Plus className="w-3.5 h-3.5" /> Añadir frase
            </button>
          </Card>
        </div>
      )}

      {/* SECTIONS VISIBLE */}
      {section === "sections" && (
        <Card title="Secciones del landing — mostrar / ocultar">
          <div className="grid md:grid-cols-2 gap-2">
            {SECTIONS.map((s) => {
              const visible = sectionsVisible[s.id] !== false;
              return (
                <button key={s.id} onClick={() => set(`landing.sections_visible.${s.id}`, !visible)}
                        data-testid={`site-section-toggle-${s.id}`}
                        className={`flex items-center justify-between gap-3 p-3 rounded-xl border hairline transition
                          ${visible ? "bg-emerald-50 border-emerald-200 hover:border-emerald-400" : "bg-zinc-100 border-zinc-300 hover:border-zinc-500"}`}>
                  <span className="text-sm font-medium">{s.label}</span>
                  {visible
                    ? <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-emerald-700"><Eye className="w-3 h-3" /> Visible</span>
                    : <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-zinc-600"><EyeOff className="w-3 h-3" /> Oculto</span>
                  }
                </button>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="border hairline bg-cream-card rounded-2xl p-4 md:p-5">
      <p className="font-mono text-[10px] uppercase tracking-wider text-coral mb-3">◊ {title}</p>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">{label}</span>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
             className="w-full bg-cream border hairline rounded-xl px-3 py-2 text-sm outline-none focus:border-coral" />
    </label>
  );
}

function Area({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">{label}</span>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} rows={3}
                className="w-full bg-cream border hairline rounded-xl px-3 py-2 text-sm outline-none focus:border-coral resize-y" />
    </label>
  );
}

function Sel({ label, testid, value, onChange, options }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
              className="w-full bg-cream border hairline rounded-xl px-3 py-2 text-sm outline-none focus:border-coral">
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </label>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: { label: "Activo", cls: "bg-emerald-100 text-emerald-700" },
    upcoming: { label: "Próximo", cls: "bg-amber-100 text-amber-800" },
    expired: { label: "Expirado", cls: "bg-zinc-200 text-zinc-700" },
  };
  const m = map[status] || map.upcoming;
  return <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${m.cls}`}>{m.label}</span>;
}
